import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FolderOffIcon from "@mui/icons-material/FolderOff";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://printformee.com/api";

const countryCodes = [
  { code: "+91", country: "India" },
  { code: "+44", country: "UK" },
  { code: "+1", country: "USA" },
];

const DeliveryEmergencyContact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    countryCode: "+91",
    phone: "",
    relationship: "other",
    deliveryId: "",
    isPrimary: false,
  });

  // Fetch emergency contacts from API
  const fetchEmergencyContacts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/delivery-emergency-contact`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("API Response:", response.data); // Debug log
      setContacts(response.data.contacts || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching emergency contacts:", error);
      setContacts([]);
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchEmergencyContacts();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFormData({
      name: "",
      countryCode: "+91",
      phone: "",
      relationship: "other",
      deliveryId: "",
      isPrimary: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.phone.trim()) {
      alert("Please fill all required fields");
      return;
    }

    // For demo purposes, if no deliveryId is provided, use a default one
    const submitData = {
      ...formData,
      deliveryId: formData.deliveryId || "65a1b2c3d4e5f6789012345", // Default delivery ID for testing
    };

    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API_BASE_URL}/delivery-emergency-contact`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Submit Response:", response.data); // Debug log

      // Refresh data
      fetchEmergencyContacts();
      handleReset();

      alert("Emergency contact added successfully");
    } catch (error) {
      console.error("Error creating emergency contact:", error);
      alert(
        error.response?.data?.message || "Error creating emergency contact. Please try again."
      );
    }
  };

  const handleDelete = async (contactId) => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      try {
        const token = localStorage.getItem("token");

        await axios.delete(
          `${API_BASE_URL}/delivery-emergency-contact/${contactId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Refresh data
        fetchEmergencyContacts();

        alert("Emergency contact deleted successfully");
      } catch (error) {
        console.error("Error deleting emergency contact:", error);
        alert(
          error.response?.data?.message || "Error deleting emergency contact",
        );
      }
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: "#F5F5F5",
        minHeight: "100vh",
      }}
    >
      {/* ----------- Top Section: Add Contact Form ----------- */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Typography
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.3rem" },
            fontWeight: "bold",
            wordBreak: "break-word",
          }}
          gutterBottom
        >
          Add New Contact Information
        </Typography>

        <Grid container spacing={2}>
          {/* Name Input */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </Grid>

          {/* Country Code Selector */}
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              name="countryCode"
              label="Code"
              value={formData.countryCode}
              onChange={handleChange}
            >
              {countryCodes.map((cc) => (
                <MenuItem key={cc.code} value={cc.code}>
                  {cc.code} ({cc.country})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12} sm={8} md={4}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </Grid>

          {/* Buttons */}
          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleReset}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ----------- Bottom Section: Table ----------- */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.3rem" },
            fontWeight: "bold",
            wordBreak: "break-word",
          }}
          gutterBottom
        >
          Contact Information Table ({contacts.length})
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 400,
            overflowX: "auto",
            borderRadius: 1,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Table sx={{ minWidth: 600 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 60 }}>SL</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>
                  Phone
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box sx={{ py: 4 }}>
                      <FolderOffIcon sx={{ fontSize: 50, color: "gray" }} />
                      <Typography>No contact found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact, index) => (
                  <TableRow key={contact._id}>
                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 60 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 120 }}>
                      {contact.name}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 150 }}>
                      {contact.fullPhone}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 80 }}>
                      <Box
                        component="span"
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          backgroundColor:
                            contact.status === "active" ? "#e8f5e8" : "#fff3e0",
                          color:
                            contact.status === "active" ? "#2e7d32" : "#f57c00",
                        }}
                      >
                        {contact.status}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 100 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(contact._id)}
                        sx={{
                          fontSize: "0.75rem",
                          borderColor: "#f44336",
                          "&:hover": {
                            borderColor: "#d32f2f",
                            backgroundColor: "#ffebee",
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DeliveryEmergencyContact;
